import React, { useEffect, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { useLanguage } from '../../lib/LanguageContext';
import { LANGUAGES } from '../../lib/translations';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const navLinks = (t) => [
  { id: 'services', label: t.nav.services },
  { id: 'about', label: t.nav.about },
  { id: 'trust', label: t.nav.trust },
  { id: 'contact', label: t.nav.contact },
];

export const Header = () => {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNav = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-cream/85 backdrop-blur-md border-b border-sand-200' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 h-28 md:h-32 flex items-center justify-between">
        <a
          href="#top"
          data-testid="logo-link"
          onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          className="flex items-center group"
        >
          <img
            src="/logo.png"
            alt="Casa Serena · KeyHolding & Property Management"
            className="h-20 md:h-24 w-auto"
            loading="eager"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-10">
          {navLinks(t).map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => handleNav(l.id)}
              className="text-sm text-ink-700 hover:text-sand-600 transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="language-switcher-trigger"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm border border-sand-200 bg-cream/60 hover:bg-cream text-sm text-ink-900 transition-colors"
            >
              <span className="font-medium tracking-wide">{lang}</span>
              <ChevronDown className="w-3.5 h-3.5 opacity-60" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[170px]">
              {LANGUAGES.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  data-testid={`language-option-${l.code.toLowerCase()}`}
                  onClick={() => setLang(l.code)}
                  className="cursor-pointer flex items-center justify-between"
                >
                  <span>{l.label}</span>
                  {lang === l.code && <Check className="w-3.5 h-3.5 text-sand-600" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            data-testid="header-cta-button"
            onClick={() => handleNav('contact')}
            className="hidden md:inline-flex items-center min-h-[44px] px-5 py-2.5 rounded-sm bg-ink-900 text-cream text-sm tracking-wide hover:bg-sand-600 transition-colors"
          >
            {t.nav.cta}
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
